import React from 'react';
import { Bell, ArrowRight, BookOpen, Store, Users, TrendingUp, Calendar, MessageCircle } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { ProgressBar } from '../components/ui/ProgressBar';
import { StatCard } from '../components/ui/StatCard';
import { TrustScore } from '../components/ui/TrustScore';
import { format, subDays } from 'date-fns';
import { Link } from 'react-router-dom';
import { useScreenInit } from '../useScreenInit';
export function Home() {
  useScreenInit();
  const user = {
    name: 'Amina',
    avatar: 'https://images.unsplash.com/photo-1531123897727-8f129e1bf98c?q=80&w=200&h=200&auto=format&fit=crop',
    supportStatus: 'Active Sponsorship',
    fundingGoal: 500,
    fundingRaised: 350,
    trustScore: 85
  };
  const quickActions = [{
    icon: <TrendingUp className="w-6 h-6" data-id="element-246" />,
    label: 'Apply Support',
    color: 'bg-brand-accent/10 text-brand-accent'
  }, {
    icon: <BookOpen className="w-6 h-6" data-id="element-247" />,
    label: 'Training',
    color: 'bg-brand-primary/10 text-brand-primary',
    to: '/app/training'
  }, {
    icon: <Store className="w-6 h-6" data-id="element-248" />,
    label: 'Marketplace',
    color: 'bg-brand-secondary/10 text-brand-secondary',
    to: '/app/marketplace'
  }, {
    icon: <Users className="w-6 h-6" data-id="element-249" />,
    label: 'My Mentor',
    color: 'bg-brand-success/10 text-brand-success'
  }];
  const activityFeed = [{
    id: 1,
    type: 'message',
    title: 'New message from Mentor Sarah',
    date: new Date(),
    icon: <MessageCircle className="w-4 h-4" data-id="element-250" />
  }, {
    id: 2,
    type: 'funding',
    title: 'Sponsorship milestone reached!',
    date: subDays(new Date(), 2),
    icon: <TrendingUp className="w-4 h-4" data-id="element-251" />
  }, {
    id: 3,
    type: 'training',
    title: 'Completed: Financial Literacy 101',
    date: subDays(new Date(), 5),
    icon: <BookOpen className="w-4 h-4" data-id="element-252" />
  }];
  return <div className="p-4 sm:p-6 space-y-6" data-id="element-253">
      {/* Header */}
      <header className="flex justify-between items-center pt-2" data-id="element-254">
        <div className="flex items-center space-x-3" data-id="element-255">
          <img src={user.avatar} alt={user.name} className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm" data-id="element-256" />
          <div data-id="element-257">
            <p className="text-sm text-text-secondary font-medium" data-id="element-258">
              Welcome back,
            </p>
            <h1 className="text-xl font-bold text-text-primary" data-id="element-259">{user.name}</h1>
          </div>
        </div>
        <button className="relative p-2 bg-white rounded-full shadow-sm text-text-secondary hover:text-brand-primary transition-colors" data-id="element-260">
          <Bell className="w-6 h-6" data-id="element-261" />
          <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-brand-danger rounded-full border-2 border-white" data-id="element-262"></span>
        </button>
      </header>

      {/* Hero / Support Status */}
      <Card className="bg-brand-primary text-white relative overflow-hidden" data-id="element-263">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-10 -mt-10 blur-2xl" data-id="element-264"></div>
        <div className="relative z-10" data-id="element-265">
          <div className="flex justify-between items-start mb-4" data-id="element-266">
            <div data-id="element-267">
              <p className="text-brand-primary-light text-sm font-medium opacity-80" data-id="element-268">
                Current Status
              </p>
              <h2 className="text-lg font-bold" data-id="element-269">{user.supportStatus}</h2>
            </div>
            <div className="bg-white/20 px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-sm" data-id="element-270">
              Phase 2
            </div>
          </div>

          <div className="mb-4" data-id="element-271">
            <div className="flex justify-between text-sm mb-1.5" data-id="element-272">
              <span data-id="element-273">Raised: ${user.fundingRaised}</span>
              <span className="opacity-80" data-id="element-274">Goal: ${user.fundingGoal}</span>
            </div>
            <ProgressBar progress={user.fundingRaised / user.fundingGoal * 100} colorClass="bg-brand-secondary" heightClass="h-2.5" data-id="element-275" />
          </div>

          <div className="flex items-center text-sm bg-white/10 rounded-xl p-3 backdrop-blur-sm" data-id="element-276">
            <Calendar className="w-4 h-4 mr-2 opacity-80" data-id="element-277" />
            <span data-id="element-278">Next milestone: Equipment Purchase in 5 days</span>
          </div>
        </div>
      </Card>

      {/* Quick Actions */}
      <section data-id="element-279">
        <h3 className="text-base font-bold text-text-primary mb-3" data-id="element-280">
          Quick Actions
        </h3>
        <div className="grid grid-cols-2 gap-3 sm:gap-4" data-id="element-281">
          {quickActions.map((action, idx) => {
          const ActionWrapper = action.to ? Link : 'div';
          return <ActionWrapper key={idx} to={action.to || '#'} className="bg-white p-4 rounded-2xl shadow-soft flex flex-col items-center justify-center text-center cursor-pointer hover:shadow-md transition-all active:scale-95" data-id="element-282">
                <div className={`p-3 rounded-xl mb-2 ${action.color}`} data-id="element-283">
                  {action.icon}
                </div>
                <span className="text-sm font-semibold text-text-primary" data-id="element-284">
                  {action.label}
                </span>
              </ActionWrapper>;
        })}
        </div>
      </section>

      {/* Statistics */}
      <section data-id="element-285">
        <div className="flex justify-between items-center mb-3" data-id="element-286">
          <h3 className="text-base font-bold text-text-primary" data-id="element-287">
            Your Progress
          </h3>
          <Link to="/app/profile" className="text-sm text-brand-primary font-medium flex items-center" data-id="element-288">
            View Profile <ArrowRight className="w-4 h-4 ml-1" data-id="element-289" />
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:gap-4" data-id="element-290">
          <StatCard title="Trainings" value="4" icon={<BookOpen className="w-5 h-5" data-id="element-292" />} trend={{
          value: '2 this month',
          isPositive: true
        }} data-id="element-291" />
          <Card className="flex flex-col justify-between items-center text-center" data-id="element-293">
            <h3 className="text-sm font-medium text-text-secondary mb-2 w-full text-left" data-id="element-294">
              Trust Score
            </h3>
            <TrustScore score={user.trustScore} size={72} strokeWidth={8} data-id="element-295" />
            <p className="text-xs text-brand-success font-medium mt-2" data-id="element-296">
              Excellent standing
            </p>
          </Card>
        </div>
      </section>

      {/* Activity Feed */}
      <section className="pb-4" data-id="element-297">
        <h3 className="text-base font-bold text-text-primary mb-3" data-id="element-298">
          Recent Activity
        </h3>
        <Card noPadding data-id="element-299">
          <div className="divide-y divide-gray-100" data-id="element-300">
            {activityFeed.map(item => <div key={item.id} className="p-4 flex items-start space-x-3 hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-301">
                <div className="mt-0.5 p-2 bg-brand-background rounded-full text-brand-primary" data-id="element-302">
                  {item.icon}
                </div>
                <div className="flex-1" data-id="element-303">
                  <p className="text-sm font-medium text-text-primary" data-id="element-304">
                    {item.title}
                  </p>
                  <p className="text-xs text-text-secondary mt-1" data-id="element-305">
                    {format(item.date, 'MMM d, yyyy • h:mm a')}
                  </p>
                </div>
              </div>)}
          </div>
          <div className="p-3 border-t border-gray-100 text-center" data-id="element-306">
            <button className="text-sm font-medium text-brand-primary hover:underline" data-id="element-307">
              View all activity
            </button>
          </div>
        </Card>
      </section>
    </div>;
}